/**
 * reCAPTCHA v2 checkbox for the dashboard's auth screens.
 *
 * Ported from recruit/src/components/Recaptcha.tsx so both products behave
 * the same way; the only real difference is the env convention (CRA's
 * REACT_APP_* rather than Vite's import.meta.env).
 *
 * FAILS OPEN BY DESIGN. With no REACT_APP_RECAPTCHA_SITE_KEY the widget
 * renders nothing and the forms do not gate on it, so sign-in keeps working
 * before the reCAPTCHA site is registered. The matching Cognito trigger
 * fails open on a missing secret for the same reason — the two have to agree,
 * or configuring one before the other locks people out.
 *
 * The token this produces is only worth anything because a PreAuthentication
 * / PreSignUp trigger verifies it server-side. A browser-side check alone
 * would be bypassed by calling Cognito directly.
 */
import {forwardRef, useEffect, useImperativeHandle, useRef} from 'react';

declare global {
  interface Window {
    grecaptcha?: {
      render: (container: HTMLElement, params: Record<string, unknown>) => number;
      reset: (widgetId?: number) => void;
    };
    __onRecaptchaLoad?: () => void;
  }
}

/** The site key is public by nature — it ships in the bundle. */
export const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

/** True when reCAPTCHA is switched on for this build. */
export const recaptchaEnabled = () => Boolean(RECAPTCHA_SITE_KEY);

let scriptPromise: Promise<void> | null = null;

/** Loads Google's script once per page, however many widgets mount. */
function loadScript(): Promise<void> {
  if (window.grecaptcha) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve) => {
      window.__onRecaptchaLoad = () => resolve();
      const s = document.createElement('script');
      s.src =
        'https://www.google.com/recaptcha/api.js?onload=__onRecaptchaLoad&render=explicit';
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

export interface RecaptchaHandle {
  /** Clears the tick so a failed attempt cannot be replayed. */
  reset: () => void;
}

interface Props {
  onChange: (token: string | null) => void;
}

const Recaptcha = forwardRef<RecaptchaHandle, Props>(function Recaptcha(
  {onChange},
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<number | null>(null);

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetId.current !== null) window.grecaptcha?.reset(widgetId.current);
      onChange(null);
    },
  }));

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY || !containerRef.current) return undefined;
    let cancelled = false;

    loadScript().then(() => {
      // Guard against a second render: grecaptcha throws if the same
      // container is rendered into twice.
      if (cancelled || !containerRef.current || widgetId.current !== null) return;
      widgetId.current = window.grecaptcha!.render(containerRef.current, {
        sitekey: RECAPTCHA_SITE_KEY,
        callback: (token: string) => onChange(token),
        // A token is good for two minutes; clear it so the form cannot be
        // submitted with one Google will reject.
        'expired-callback': () => onChange(null),
        'error-callback': () => onChange(null),
      });
    });

    return () => {
      cancelled = true;
    };
  }, [onChange]);

  if (!RECAPTCHA_SITE_KEY) return null;
  return <div ref={containerRef} className="flex justify-center" />;
});

export default Recaptcha;
